import React, { useState, useCallback, useEffect, useRef } from 'react';
import { planFileService } from '../../services/endpoints/planFileService';
import { CoachPlanFile } from '../../types';

const MAX_FILE_SIZE_BYTES = 50 * 1024 * 1024; // 50 MB
const MAX_FILES_PER_ATHLETE = 5;

const ALLOWED_TYPES = [
  'application/pdf',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.ms-excel',
  'image/jpeg',
  'image/jpg',
  'image/png',
];

interface Props {
  athleteUserId: string;
  athleteName: string;
  onClose: () => void;
}

const CoachPlanFilesDrawer: React.FC<Props> = ({ athleteUserId, athleteName, onClose }) => {
  const [files, setFiles]         = useState<CoachPlanFile[]>([]);
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState<string | null>(null);

  // Upload form
  const [selectedFile, setSelectedFile]     = useState<File | null>(null);
  const [description, setDescription]       = useState('');
  const [uploading, setUploading]           = useState(false);
  const [uploadError, setUploadError]       = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Delete confirm
  const [deleteTargetId, setDeleteTargetId] = useState<string | null>(null);

  // Download tracking
  const [downloading, setDownloading]       = useState<string | null>(null);

  const loadFiles = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await planFileService.listForAthlete(athleteUserId);
      setFiles(data);
    } catch {
      setError('Failed to load plan files.');
    } finally {
      setLoading(false);
    }
  }, [athleteUserId]);

  useEffect(() => { loadFiles(); }, [loadFiles]);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    setUploadError(null);
    const file = e.target.files?.[0] ?? null;
    if (!file) return;

    if (file.size > MAX_FILE_SIZE_BYTES) {
      setUploadError('File exceeds 50 MB limit.');
      setSelectedFile(null);
      return;
    }
    if (!ALLOWED_TYPES.includes(file.type)) {
      setUploadError('Unsupported file type. Please upload a PDF, Word document, Excel spreadsheet, or image.');
      setSelectedFile(null);
      return;
    }
    setSelectedFile(file);
  };

  const handleUpload = async () => {
    if (!selectedFile) return;
    setUploading(true);
    setUploadError(null);
    try {
      const created = await planFileService.uploadFile(
        athleteUserId,
        selectedFile,
        description.trim() || undefined
      );
      setFiles(prev => [created, ...prev]);
      setSelectedFile(null);
      setDescription('');
      if (fileInputRef.current) fileInputRef.current.value = '';
    } catch {
      setUploadError('Failed to upload file. Make sure you have an active relationship with this athlete.');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (planFileId: string) => {
    try {
      await planFileService.deleteFile(planFileId);
      setFiles(prev => prev.filter(f => f.planFileId !== planFileId));
      setDeleteTargetId(null);
    } catch {
      setError('Failed to delete file.');
    }
  };

  const handleDownload = async (file: CoachPlanFile) => {
    setDownloading(file.planFileId);
    try {
      await planFileService.downloadFile(file.planFileId, file.originalFileName);
    } catch {
      setError('Failed to download file.');
    } finally {
      setDownloading(null);
    }
  };

  return (
    <>
      {/* Overlay */}
      <div className="fixed inset-0 bg-black/50 z-40" onClick={onClose} />

      {/* Drawer */}
      <div className="fixed top-0 right-0 h-full w-full max-w-md bg-dark-800 border-l border-dark-700 z-50 flex flex-col shadow-2xl">

        {/* Header */}
        <div className="flex items-start justify-between px-5 py-4 border-b border-dark-700 shrink-0">
          <div>
            <h2 className="text-lg font-semibold text-white">Plan Files</h2>
            <p className="text-sm text-gray-400 mt-0.5">{athleteName}</p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-300 transition-colors text-xl leading-none mt-0.5"
          >
            ×
          </button>
        </div>

        {/* Scrollable body */}
        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-5">

          {/* ── Upload Form ── */}
          <div className="bg-dark-700 rounded-xl p-4 space-y-3">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-medium text-gray-300">Upload Plan File</h3>
              <span className={`text-xs ${files.length >= MAX_FILES_PER_ATHLETE ? 'text-red-400' : 'text-gray-500'}`}>
                {files.length} / {MAX_FILES_PER_ATHLETE} files
              </span>
            </div>
            <p className="text-xs text-gray-500">PDF, Word, Excel, or image · max 50 MB</p>

            {/* File picker */}
            <div>
              <input
                ref={fileInputRef}
                type="file"
                accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png"
                onChange={handleFileSelect}
                disabled={files.length >= MAX_FILES_PER_ATHLETE}
                className="hidden"
                id="plan-file-input"
              />
              <label
                htmlFor="plan-file-input"
                className={`inline-flex items-center gap-2 px-3 py-2 text-sm bg-dark-800 border rounded-lg transition-colors ${
                  files.length >= MAX_FILES_PER_ATHLETE
                    ? 'border-dark-700 text-gray-600 cursor-not-allowed'
                    : 'border-dark-700 hover:border-gold-500/40 text-gray-300 hover:text-white cursor-pointer'
                }`}
              >
                <span>📎</span>
                <span>Choose file</span>
              </label>
              {selectedFile && (
                <p className="mt-2 text-sm text-gray-300 truncate">
                  {selectedFile.name}
                  <span className="ml-2 text-xs text-gray-500">
                    ({formatFileSize(selectedFile.size)})
                  </span>
                </p>
              )}
            </div>

            {/* Optional description */}
            <textarea
              value={description}
              onChange={e => setDescription(e.target.value)}
              placeholder="Add a description (optional)..."
              rows={2}
              maxLength={1000}
              className="w-full bg-dark-800 border border-dark-700 rounded-lg px-3 py-2 text-sm text-white placeholder-gray-500 focus:outline-none focus:border-gold-500 resize-none"
            />

            {uploadError && (
              <p className="text-red-400 text-sm">{uploadError}</p>
            )}

            {files.length >= MAX_FILES_PER_ATHLETE && (
              <p className="text-amber-400 text-xs">
                Limit reached. Delete a file to upload a new one.
              </p>
            )}

            <div className="flex justify-end">
              <button
                onClick={handleUpload}
                disabled={uploading || !selectedFile || files.length >= MAX_FILES_PER_ATHLETE}
                className="px-4 py-1.5 bg-gold-500 hover:bg-gold-400 disabled:bg-dark-800 disabled:text-gray-500 text-black text-sm font-semibold rounded-lg transition-colors"
              >
                {uploading ? 'Uploading...' : 'Upload'}
              </button>
            </div>
          </div>

          {/* ── Files List ── */}
          {error && <p className="text-red-400 text-sm">{error}</p>}

          {loading ? (
            <p className="text-gray-500 text-sm text-center py-6">Loading files...</p>
          ) : files.length === 0 ? (
            <p className="text-gray-500 text-sm text-center py-6">
              No plan files shared yet.
            </p>
          ) : (
            <div className="space-y-3">
              {files.map(file => (
                <PlanFileCard
                  key={file.planFileId}
                  file={file}
                  confirmingDelete={deleteTargetId === file.planFileId}
                  isDownloading={downloading === file.planFileId}
                  onDownload={() => handleDownload(file)}
                  onRequestDelete={() => setDeleteTargetId(file.planFileId)}
                  onCancelDelete={() => setDeleteTargetId(null)}
                  onConfirmDelete={() => handleDelete(file.planFileId)}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </>
  );
};

// ── PlanFileCard ──────────────────────────────────────────────────────────────

interface CardProps {
  file: CoachPlanFile;
  confirmingDelete: boolean;
  isDownloading: boolean;
  onDownload: () => void;
  onRequestDelete: () => void;
  onCancelDelete: () => void;
  onConfirmDelete: () => void;
}

const PlanFileCard: React.FC<CardProps> = ({
  file,
  confirmingDelete,
  isDownloading,
  onDownload,
  onRequestDelete,
  onCancelDelete,
  onConfirmDelete,
}) => {
  const uploaded = new Date(file.uploadedAt).toLocaleDateString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric',
  });

  return (
    <div className="bg-dark-700 rounded-xl p-4 space-y-2">
      <div className="flex items-start gap-3">
        <span className="text-2xl shrink-0">{fileIcon(file.contentType)}</span>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-medium text-white truncate" title={file.originalFileName}>
            {file.originalFileName}
          </p>
          <p className="text-xs text-gray-500 mt-0.5">
            {formatFileSize(file.fileSizeBytes)} · {uploaded}
          </p>
          {file.description && (
            <p className="text-xs text-gray-400 mt-1 line-clamp-2">{file.description}</p>
          )}
        </div>
      </div>

      <div className="flex items-center justify-between pt-1">
        <button
          onClick={onDownload}
          disabled={isDownloading}
          className="text-xs text-blue-400 hover:text-blue-300 disabled:text-gray-500 transition-colors"
        >
          {isDownloading ? 'Downloading...' : 'Download'}
        </button>

        {confirmingDelete ? (
          <div className="flex items-center gap-2">
            <span className="text-xs text-red-400">Delete this file?</span>
            <button
              onClick={onCancelDelete}
              className="text-xs text-gray-400 hover:text-gray-200 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={onConfirmDelete}
              className="text-xs px-2 py-0.5 bg-red-600 hover:bg-red-500 text-white rounded transition-colors"
            >
              Delete
            </button>
          </div>
        ) : (
          <button
            onClick={onRequestDelete}
            className="text-xs text-gray-500 hover:text-red-400 transition-colors"
          >
            Delete
          </button>
        )}
      </div>
    </div>
  );
};

// ── Utilities ─────────────────────────────────────────────────────────────────

function fileIcon(contentType: string): string {
  if (contentType === 'application/pdf') return '📄';
  if (contentType.includes('spreadsheet') || contentType.includes('excel')) return '📊';
  if (contentType.includes('word') || contentType.includes('msword')) return '📝';
  if (contentType.startsWith('image/')) return '🖼';
  return '📎';
}

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export default CoachPlanFilesDrawer;
